/**
 * Page shell for signed-in portal routes.
 *
 * A persistent left sidebar (brand, Account / Knowledge / MCP nav, Sign
 * out) plus a slim top header (breadcrumb, language toggle, avatar) wrap
 * the white content well — distinct from the wizard's chrome-free
 * `Layout`, which is intentionally focused for the one-shot onboarding
 * ceremony.
 *
 * Sign out invokes MSAL's full logout redirect so the user is signed
 * out of Microsoft for this app and returns to the public landing
 * (shared-machine hygiene). When MSAL is not configured — local dev
 * with `FINN_PORTAL_AUTH_DISABLED` — the button is rendered disabled.
 */
import { type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { Icon, LanguageToggle } from "@/components/ui";
import { useI18n } from "@/lib/i18n";

function SidebarLink({
  to,
  icon,
  label,
}: {
  to: string;
  icon: string;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        [
          "flex items-center gap-3 rounded-lg px-3 py-2 text-body-main transition-colors",
          isActive
            ? "bg-on-background text-surface"
            : "text-on-surface-variant hover:bg-surface-container-highest",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <Icon name={icon} filled={isActive} className="text-[20px]" />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

function SignOutRow({
  onClick,
  disabled,
}: {
  onClick?: () => void;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-body-main text-on-surface-variant transition-colors hover:bg-surface-container-highest disabled:opacity-50"
    >
      <Icon name="logout" className="text-[20px]" />
      <span>{t("sidebar.signOut")}</span>
    </button>
  );
}

// Two variants — one calls useMsal, the other never does — so React's
// rules-of-hooks are not violated when MsalProvider is absent in
// auth-disabled local dev.
function SignOutWithMsal() {
  const { instance } = useMsal();
  return (
    <SignOutRow
      onClick={() =>
        instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + "/",
        })
      }
    />
  );
}

function SignOutDisabled() {
  return <SignOutRow disabled />;
}

function Sidebar({ authConfigured }: { authConfigured: boolean }) {
  const { t } = useI18n();
  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-[240px] flex-col gap-1 border-r border-outline-variant bg-surface-container-low p-4">
      <Link to="/" className="mb-8 flex items-center gap-3 px-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-on-background text-base font-bold text-surface">
          F
        </span>
        <span className="text-page-title font-bold text-on-surface">Finn</span>
      </Link>
      <nav className="flex flex-1 flex-col gap-1">
        <SidebarLink to="/account" icon="account_circle" label={t("sidebar.account")} />
        <SidebarLink to="/knowledge" icon="menu_book" label={t("sidebar.knowledge")} />
        <SidebarLink to="/mcp" icon="dns" label={t("sidebar.mcp")} />
      </nav>
      <div className="mt-auto border-t border-outline-variant pt-4">
        {authConfigured ? <SignOutWithMsal /> : <SignOutDisabled />}
      </div>
    </aside>
  );
}

function Breadcrumb() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const labels: Record<string, string> = {
    "/account": t("sidebar.account"),
    "/knowledge": t("sidebar.knowledge"),
    "/mcp": t("sidebar.mcp"),
  };
  const current = labels[pathname];
  return (
    <span className="text-label-muted text-on-surface-variant">
      Finn{current ? ` / ${current}` : ""}
    </span>
  );
}

export function AuthenticatedLayout({
  authConfigured,
  children,
}: {
  authConfigured: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="min-h-full bg-surface-container-lowest">
      <Sidebar authConfigured={authConfigured} />

      <header className="fixed left-[240px] right-0 top-0 z-30 flex h-14 items-center justify-between border-b border-outline-variant bg-surface-container-lowest px-6">
        <Breadcrumb />
        <div className="flex items-center gap-4">
          <LanguageToggle />
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-container text-on-surface-variant">
            <Icon name="person" className="text-[20px]" />
          </span>
        </div>
      </header>

      <main className="ml-[240px] min-h-screen pt-14">
        <div className="mx-auto w-full max-w-[720px] px-6 py-8">
          {children ?? <Outlet />}
        </div>
      </main>
    </div>
  );
}
